import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Form, Modal, Row, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BsArrowLeft, BsPencil, BsPlus, BsToggleOff, BsToggleOn } from "react-icons/bs";
import Cabecalho from "../../../components/Cabecalho/Cabecalho";
import api from "../../../Utils/api";
import { hasPermission } from '../../../Utils/permissions';
import "./naturezas.css";

const vazio = { nome: "" };

function normalizar(nome) {
  return String(nome || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .trim().replace(/\s+/g, " ").toLocaleLowerCase("pt-BR");
}

export default function Naturezas() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [history, setHistory] = useState(null);
  const [inspection, setInspection] = useState(null);
  const loadSequence = React.useRef(0);
  const [form, setForm] = useState(vazio);
  const [editingId, setEditingId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = async () => {
    const sequence = ++loadSequence.current;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/naturezas", { params: { incluirInativas: true, q: search || undefined, page, status: status || undefined } });
      if (sequence !== loadSequence.current) return;
      setItems(data.items || []);
      setHasMore(Boolean(data.hasMore));
    } catch (requestError) {
      if (sequence === loadSequence.current) setError(requestError.response?.data?.message || "Não foi possível carregar as naturezas.");
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => {
      clearTimeout(timer);
      // This ref is a request generation, not a DOM node. Invalidate responses
      // when changing filters or unmounting, including before the next debounce.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      loadSequence.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, status]);

  const possibleDuplicate = useMemo(() => {
    const key = normalizar(form.nome);
    return key && items.some((item) => item._id !== editingId && item.normalizado === key);
  }, [editingId, form.nome, items]);

  const updateField = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const startEdit = (item) => {
    setEditingId(item._id);
    setForm({ nome: item.nome });
    setError("");
    setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearForm = () => {
    setEditingId("");
    setForm(vazio);
    setError("");
  };

  const save = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    if (possibleDuplicate) {
      setError("Já existe uma natureza com esse nome.");
      return;
    }
    const payload = { nome: form.nome.trim() };
    setSaving(true);
    try {
      if (editingId) await api.put(`/naturezas/${editingId}`, payload);
      else await api.post("/naturezas", payload);
      setNotice(editingId ? "Natureza atualizada." : "Natureza cadastrada.");
      clearForm();
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Não foi possível salvar a natureza.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    setError("");
    setNotice("");
    try {
      await api.patch(`/naturezas/${item._id}/status`, { ativo: !item.ativo });
      setNotice(`Natureza ${item.ativo ? "desativada" : "ativada"}.`);
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Não foi possível alterar o status.");
    }
  };

  const moderate = async (item, status, reject = false) => {
    const justificativa = reject ? window.prompt('Justificativa da rejeição (5 a 1000 caracteres):') : undefined;
    if (reject && !justificativa) return;
    try {
      await api.patch(`/naturezas/${item._id}/status`, { status, ...(reject ? { acao: 'rejeitar', justificativa } : {}) });
      setNotice(reject ? 'Cadastro rejeitado; referências históricas preservadas.' : 'Natureza aprovada.'); await load();
    } catch (error) { setError(error.response?.data?.message || 'Não foi possível atualizar.'); }
  };
  const showHistory = async item => {
    try { const { data } = await api.get(`/naturezas/${item._id}/historico`); setHistory(data.historico || []); }
    catch (error) { setError(error.response?.data?.message || 'Falha ao consultar histórico.'); }
  };
  const inspect = async (item, kind, page = 1) => {
    try {
      const { data } = await api.get(kind === 'legadas' ? '/naturezas/legadas' : `/naturezas/${item._id}/${kind}`, { params: { page } });
      setInspection({ item, kind, page, ...data });
    } catch (error) { setError(error.response?.data?.message || 'Falha na consulta.'); }
  };
  const associate = async (item, suggestedName) => {
    const nome = window.prompt('Nome da natureza oficial equivalente (a busca ignora acentos):', suggestedName || item.nome);
    if (!nome) return;
    try {
      const { data } = await api.get('/naturezas', { params: { q: nome, incluirInativas: true, status: 'oficial', page: 1 } });
      const target = data.items.find(candidate => candidate._id !== item._id && candidate.ativo && normalizar(candidate.nome) === normalizar(nome));
      if (!target) { setError('Nenhuma natureza oficial com esse nome. Confira a nomenclatura no catálogo.'); return; }
      const path = `/naturezas/${item._id}/associar`;
      const preview = await api.post(path, { equivalenteId: target._id });
      if (!window.confirm(`Associar "${item.nome}" a "${target.nome}"? ${preview.data.afetados} boletim(ns) referenciam a origem. Os boletins históricos serão preservados e a origem será desativada.`)) return;
      await api.post(path, { equivalenteId: target._id, confirmar: true });
      setNotice('Associação registrada com histórico.'); await load();
    } catch (error) { setError(error.response?.data?.message || 'Falha ao associar.'); }
  };


  return (
    <>
      <Cabecalho />
      <main className="naturezas-page">
        <Modal show={Boolean(inspection)} onHide={() => setInspection(null)} size="lg">
          <Modal.Header closeButton><Modal.Title>{inspection?.kind === 'legadas' ? 'Grafias históricas sem referência' : inspection?.kind === 'impacto' ? 'Boletins que referenciam esta natureza' : 'Possíveis equivalências para revisão'}</Modal.Title></Modal.Header>
          <Modal.Body>
            <p>As associações preservam o conteúdo e as referências dos boletins existentes.</p>
            {(inspection?.items || []).map(item => <div key={item._id} className="mb-3">
              {inspection.kind === 'legadas' ? <span>{item.nome || 'Natureza não informada'}</span> : inspection.kind === 'impacto' ? <span>{item.numero} — {item.data} — {item.municipio}: {item.natureza}{item.deletedAt ? ' (excluído logicamente)' : ''}</span> :
                <><span>{item.nome} ({item.status})</span> {item.ativo && item.status === 'oficial' && <Button size="sm" className="ms-2" onClick={() => associate(inspection.item, item.nome)}>Associar</Button>}</>}
            </div>)}
            {!inspection?.items?.length && <p>Nenhum registro encontrado.</p>}
            {['impacto', 'legadas'].includes(inspection?.kind) && <div className="d-flex gap-2">
              <Button disabled={inspection.page <= 1} onClick={() => inspect(inspection.item, inspection.kind, inspection.page - 1)}>Anterior</Button>
              <Button disabled={!inspection.hasMore} onClick={() => inspect(inspection.item, inspection.kind, inspection.page + 1)}>Próxima</Button>
            </div>}
          </Modal.Body>
        </Modal>
        <Container>
          <div className="naturezas-heading">
            <div>
              <p className="dashboard-eyebrow">Administração</p>
              <h2>Catálogo de naturezas</h2>
              <p>Cada nome aparece uma única vez no catálogo. A duplicidade ignora acentos e diferenças entre maiúsculas e minúsculas.</p>
            </div>
            <Button variant="outline-secondary" onClick={() => navigate("/administrador/opcoes-boletim")}>
              <BsArrowLeft className="me-2" /> Opções do Boletim
            </Button>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          {notice && <Alert variant="success">{notice}</Alert>}
          <Button variant="outline-secondary" className="mb-3" onClick={() => inspect(null, 'legadas')}>Consultar grafias históricas sem referência</Button>
          {history && <Alert variant="info"><h3>Histórico de alterações</h3>
            <ul>{history.map((event, i) => <li key={i}>{new Date(event.data).toLocaleString('pt-BR')} - {event.acao} - {event.usuario}{event.justificativa ? `: ${event.justificativa}` : ''}</li>)}</ul>
            {!history.length && <p>Sem alterações registradas. Cadastro legado.</p>}
            <Button onClick={() => setHistory(null)}>Fechar histórico</Button></Alert>}
          <section className="naturezas-card">
            <h3>{editingId ? "Editar natureza" : "Cadastrar natureza"}</h3>
            <Form onSubmit={save}>
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Nome</Form.Label>
                    <Form.Control required maxLength={500} value={form.nome} onChange={updateField("nome")} />
                  </Form.Group>
                </Col>
              </Row>
              {possibleDuplicate && <Alert variant="warning" className="mt-3 mb-0">Possível duplicidade: já existe uma natureza com esse nome normalizado.</Alert>}
              <div className="naturezas-form-actions">
                <Button type="submit" disabled={saving || possibleDuplicate}>
                  {editingId ? "Salvar alterações" : <><BsPlus className="me-1" /> Cadastrar</>}
                </Button>
                {editingId && <Button type="button" variant="outline-secondary" onClick={clearForm}>Cancelar edição</Button>}
              </div>
            </Form>
          </section>

          <section className="naturezas-card mt-4">
            <div className="naturezas-list-heading">
              <h3>Naturezas cadastradas</h3>
              <Form.Control
                type="search"
                aria-label="Pesquisar naturezas"
                placeholder="Pesquisar por nome"
                value={search}
                onChange={(event) => { setSearch(event.target.value); setPage(1); }}
              />
            </div>
            <Form.Select aria-label="Filtrar por status" value={status} onChange={event => { setStatus(event.target.value); setPage(1); }} className="mb-3">
              <option value="">Todos os status</option><option value="oficial">Oficiais</option><option value="pendente">Pendentes</option><option value="inativa">Inativas</option>
            </Form.Select>
            {loading ? <p role="status">Carregando catálogo...</p> : (
              <Table responsive hover className="mb-0 align-middle">
                <thead><tr><th>Nome</th><th>Status</th><th>Ações</th></tr></thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <strong>{item.nome}</strong>
                      </td>
                      <td>{item.status || (item.ativo ? "oficial" : "inativa")}</td>
                      <td>
                        <div className="d-flex gap-2 flex-wrap">
                          {item.status === 'pendente' && <>
                            <Button size="sm" onClick={() => moderate(item, 'oficial')}>Aprovar</Button>
                            <Button size="sm" variant="outline-danger" onClick={() => moderate(item, 'inativa', true)}>Rejeitar</Button>
                          </>}
                          <Button size="sm" variant="outline-secondary" onClick={() => showHistory(item)}>Histórico</Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => inspect(item, 'duplicidades')}>Possíveis duplicidades</Button>
                          {hasPermission('boletins.admin.view') && <Button size="sm" variant="outline-secondary" onClick={() => inspect(item, 'impacto')}>Boletins afetados</Button>}
                          <Button size="sm" variant="outline-secondary" onClick={() => associate(item)}>Associar equivalente</Button>
                          <Button size="sm" variant="outline-primary" aria-label={`Editar ${item.nome}`} onClick={() => startEdit(item)}><BsPencil /></Button>
                          <Button size="sm" variant={item.ativo ? "outline-danger" : "outline-success"} aria-label={`${item.ativo ? "Desativar" : "Ativar"} ${item.nome}`} onClick={() => toggleActive(item)}>
                            {item.ativo ? <BsToggleOff /> : <BsToggleOn />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!items.length && <tr><td colSpan="3" className="text-center">Nenhuma natureza cadastrada.</td></tr>}
                </tbody>
              </Table>
            )}
            <div className="d-flex gap-2 mt-3 align-items-center">
              <Button disabled={loading || page === 1} onClick={() => setPage(value => value - 1)}>Anterior</Button>
              <span>Página {page}</span><Button disabled={loading || !hasMore} onClick={() => setPage(value => value + 1)}>Próxima</Button>
            </div>
          </section>
        </Container>
      </main>
    </>
  );
}
